import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageBySlug } from "@/hooks/usePagesDB";
import NotFound from "../NotFound";
import AdSlotDisplay from "@/components/feature/AdSlotDisplay";

const adFormats = [
  {
    name: "Header Banner",
    size: "970 × 250",
    position: "Oben auf allen Seiten",
    device: "Desktop",
    price: "CHF 4'500 / Monat",
    icon: "ri-layout-top-2-line",
  },
  {
    name: "Top Leaderboard",
    size: "728 × 90",
    position: "Unter der Navigation",
    device: "Desktop",
    price: "CHF 3'200 / Monat",
    icon: "ri-layout-top-line",
  },
  {
    name: "Sidebar Rectangle",
    size: "300 × 250",
    position: "Sidebar oben",
    device: "Desktop",
    price: "CHF 2'800 / Monat",
    icon: "ri-layout-right-line",
  },
  {
    name: "Sidebar Half-Page",
    size: "300 × 600",
    position: "Sidebar gross",
    device: "Desktop",
    price: "CHF 3'800 / Monat",
    icon: "ri-layout-right-2-line",
  },
  {
    name: "In-Article Ad",
    size: "728 × 90",
    position: "Innerhalb von Artikeln",
    device: "Desktop & Mobile",
    price: "CHF 2'500 / Monat",
    icon: "ri-article-line",
  },
  {
    name: "Mobile Top Banner",
    size: "320 × 100",
    position: "Oben auf Mobilgeräten",
    device: "Mobile",
    price: "CHF 2'200 / Monat",
    icon: "ri-smartphone-line",
  },
];

const stats = [
  { label: "Monatliche Besucher", value: "500K+", icon: "ri-user-line" },
  { label: "Seitenaufrufe / Monat", value: "2.5M+", icon: "ri-eye-line" },
  { label: "Newsletter-Abonnenten", value: "180K", icon: "ri-mail-line" },
  { label: "Durchschnittliche Session", value: "4:30 min", icon: "ri-time-line" },
  { label: "Social Media Follower", value: "85K", icon: "ri-share-line" },
  { label: "Veröffentlichte Artikel", value: "12K+", icon: "ri-article-line" },
];

export default function MediaKitPage() {
  const { page } = usePageBySlug("werbung");
  const [formData, setFormData] = useState({
    company: "",
    name: "",
    email: "",
    phone: "",
    interest: "",
    budget: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  // Hide page if unpublished
  if (page && !page.isPublished) return <NotFound />;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white pt-[80px] md:pt-[130px]">
      {/* Hero */}
      <div className="bg-hj-navy text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-16">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-white/60 mb-4 font-body" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Startseite</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-white/80">{page?.title ?? "Werbung & Media Kit"}</span>
          </nav>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold font-heading mb-4">
            {page?.title ?? "Werbung & Media Kit"}
          </h1>
          {page?.content && (
            <p className="text-base md:text-lg text-white/70 max-w-2xl font-body leading-relaxed">
              {page.content}
            </p>
          )}
        </div>
      </div>

      {/* Stats */}
      <section className="py-8 md:py-12 bg-hj-gray-light">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-white rounded-xl p-4 md:p-5 border border-hj-gray-border text-center">
                <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-hj-red/10 flex items-center justify-center">
                  <i className={`${stat.icon} text-hj-red text-lg`}></i>
                </div>
                <p className="text-xl md:text-2xl font-bold text-hj-dark font-heading">{stat.value}</p>
                <p className="text-[11px] md:text-xs text-hj-gray mt-1 font-body">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-hj-dark mb-4 font-heading">
                Unsere Zielgruppe
              </h2>
              <p className="text-hj-gray leading-relaxed mb-6 font-body">
                Die Leser von Helvetik Journal sind gut informierte, entscheidungsfreudige Menschen,
                die täglich nach relevanten Informationen suchen. Sie sind in der Schweiz ansässig,
                interessieren sich für Politik, Wirtschaft, Technologie und Gesellschaft – und treffen
                Kaufentscheidungen auf Basis vertrauenswürdiger Informationen.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Alter", value: "35–54 Jahre" },
                  { label: "Geschlecht", value: "55% M / 45% F" },
                  { label: "Region", value: "Deutschschweiz" },
                  { label: "Einkommen", value: "Überdurchschnittlich" },
                  { label: "Bildung", value: "Hochschulabschluss" },
                  { label: "Gerät", value: "60% Mobile / 40% Desktop" },
                ].map((item) => (
                  <div key={item.label} className="bg-hj-gray-light rounded-lg p-3">
                    <p className="text-[10px] text-hj-gray uppercase tracking-wider font-body">{item.label}</p>
                    <p className="text-sm font-semibold text-hj-dark font-body">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20Swiss%20business%20team%20meeting%20modern%20office%20Zurich%20diverse%20group%20collaboration%20strategy%20discussion%20bright%20natural%20lighting%20contemporary%20interior%20design&width=600&height=400&seq=mediakit1&orientation=landscape"
                alt="Helvetik Journal Zielgruppe"
                loading="lazy"
                width={600}
                height={400}
                className="w-full rounded-xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Ad Formats */}
      <section className="py-8 md:py-12 bg-hj-gray-light">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-bold text-hj-dark mb-6 md:mb-8 font-heading">
            Werbeformate & Platzierungen
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {adFormats.map((format) => (
              <div key={format.name} className="bg-white rounded-xl border border-hj-gray-border p-5 hover:shadow-sm transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-hj-red/10 flex items-center justify-center">
                    <i className={`${format.icon} text-hj-red text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-hj-dark font-heading">{format.name}</h3>
                    <p className="text-xs text-hj-gray font-body">{format.size}</p>
                  </div>
                </div>
                <div className="space-y-1.5 mb-4">
                  <p className="text-xs text-hj-gray font-body">
                    <i className="ri-map-pin-line text-hj-muted mr-1"></i>
                    {format.position}
                  </p>
                  <p className="text-xs text-hj-gray font-body">
                    <i className="ri-device-line text-hj-muted mr-1"></i>
                    {format.device}
                  </p>
                </div>
                <div className="pt-3 border-t border-hj-gray-border">
                  <p className="text-sm font-bold text-hj-dark font-body">{format.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Banner Examples */}
      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-bold text-hj-dark mb-6 md:mb-8 font-heading">
            Banner-Beispiele
          </h2>
          <div className="space-y-6">
            {/* Leaderboard */}
            <div>
              <p className="text-sm font-medium text-hj-dark mb-2 font-body">Leaderboard (728 × 90)</p>
              <div className="bg-hj-gray-light rounded-lg p-4 flex items-center justify-center">
                <div className="w-full max-w-[728px] h-[90px] bg-hj-dark rounded flex items-center justify-center text-white text-sm font-body">
                  <span>728 × 90 – Ihre Werbung hier</span>
                </div>
              </div>
            </div>
            {/* Rectangle */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-hj-dark mb-2 font-body">Rectangle (300 × 250)</p>
                <div className="bg-hj-gray-light rounded-lg p-4 flex items-center justify-center">
                  <div className="w-[300px] h-[250px] bg-hj-dark rounded flex items-center justify-center text-white text-sm font-body">
                    <span>300 × 250</span>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-hj-dark mb-2 font-body">Half-Page (300 × 600)</p>
                <div className="bg-hj-gray-light rounded-lg p-4 flex items-center justify-center">
                  <div className="w-[300px] h-[300px] bg-hj-dark rounded flex items-center justify-center text-white text-sm font-body md:h-[600px]">
                    <span>300 × 600</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Mobile */}
            <div>
              <p className="text-sm font-medium text-hj-dark mb-2 font-body">Mobile Banner (320 × 100)</p>
              <div className="bg-hj-gray-light rounded-lg p-4 flex items-center justify-center">
                <div className="w-[320px] h-[100px] bg-hj-dark rounded flex items-center justify-center text-white text-sm font-body">
                  <span>320 × 100 – Mobile</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-8 md:py-12 bg-hj-gray-light">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="bg-white rounded-xl border border-hj-gray-border p-6 md:p-8">
            <h2 className="text-xl md:text-2xl font-bold text-hj-dark mb-2 font-heading">
              Werbeanfrage
            </h2>
            <p className="text-sm text-hj-gray mb-6 font-body">
              Interessiert an einer Partnerschaft? Füllen Sie das Formular aus und wir melden uns innerhalb von 24 Stunden bei Ihnen.
            </p>

            {submitted ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-100 flex items-center justify-center">
                  <i className="ri-check-line text-xl text-emerald-600"></i>
                </div>
                <h3 className="text-lg font-bold text-emerald-800 mb-1 font-heading">Anfrage gesendet!</h3>
                <p className="text-sm text-emerald-700 font-body">
                  Vielen Dank für Ihr Interesse. Unser Werbeteam wird sich innerhalb von 24 Stunden bei Ihnen melden.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">Unternehmen</label>
                    <input
                      type="text"
                      required
                      value={formData.company}
                      onChange={(e) => setFormData((p) => ({ ...p, company: e.target.value }))}
                      className="w-full px-4 py-2.5 text-sm border border-hj-gray-border rounded-lg focus:outline-none focus:border-hj-red focus:ring-1 focus:ring-hj-red/20 font-body"
                      placeholder="Firmenname"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">Ihr Name</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                      className="w-full px-4 py-2.5 text-sm border border-hj-gray-border rounded-lg focus:outline-none focus:border-hj-red focus:ring-1 focus:ring-hj-red/20 font-body"
                      placeholder="Vor- und Nachname"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">E-Mail</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                      className="w-full px-4 py-2.5 text-sm border border-hj-gray-border rounded-lg focus:outline-none focus:border-hj-red focus:ring-1 focus:ring-hj-red/20 font-body"
                      placeholder="ihre@firma.ch"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">Telefon</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                      className="w-full px-4 py-2.5 text-sm border border-hj-gray-border rounded-lg focus:outline-none focus:border-hj-red focus:ring-1 focus:ring-hj-red/20 font-body"
                      placeholder="+41 44 123 45 67"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">Interesse</label>
                    <select
                      required
                      value={formData.interest}
                      onChange={(e) => setFormData((p) => ({ ...p, interest: e.target.value }))}
                      className="w-full px-4 py-2.5 text-sm border border-hj-gray-border rounded-lg focus:outline-none focus:border-hj-red focus:ring-1 focus:ring-hj-red/20 font-body bg-white"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="display">Display-Werbung</option>
                      <option value="native">Native Advertising</option>
                      <option value="newsletter">Newsletter-Werbung</option>
                      <option value="video">Video-Pre-Roll</option>
                      <option value="social">Social Media Promotion</option>
                      <option value="other">Sonstiges</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">Geschätztes Budget</label>
                    <select
                      value={formData.budget}
                      onChange={(e) => setFormData((p) => ({ ...p, budget: e.target.value }))}
                      className="w-full px-4 py-2.5 text-sm border border-hj-gray-border rounded-lg focus:outline-none focus:border-hj-red focus:ring-1 focus:ring-hj-red/20 font-body bg-white"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="<5000">Unter CHF 5'000</option>
                      <option value="5000-15000">CHF 5'000 – 15'000</option>
                      <option value="15000-50000">CHF 15'000 – 50'000</option>
                      <option value=">50000">Über CHF 50'000</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">Nachricht</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData((p) => ({ ...p, message: e.target.value }))}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm border border-hj-gray-border rounded-lg focus:outline-none focus:border-hj-red focus:ring-1 focus:ring-hj-red/20 font-body resize-none"
                    placeholder="Beschreiben Sie kurz Ihre Werbevorstellungen..."
                  />
                  <p className="text-xs text-hj-muted mt-1 font-body">{formData.message.length}/500 Zeichen</p>
                </div>
                <button
                  type="submit"
                  className="px-8 py-3 bg-hj-red text-white text-sm font-semibold rounded-lg hover:bg-hj-red-light transition-colors font-body whitespace-nowrap"
                >
                  Anfrage senden
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Direct Contact */}
      <section className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-hj-dark mb-4 font-heading">
            Direkter Kontakt
          </h2>
          <p className="text-sm text-hj-gray mb-6 font-body">
            Bevorzugen Sie eine direkte Kontaktaufnahme? Unser Werbeteam steht Ihnen gerne zur Verfügung.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="mailto:werbung@helvetik-journal.ch"
              className="flex items-center gap-2 px-6 py-3 bg-hj-red text-white text-sm font-medium rounded-lg hover:bg-hj-red-light transition-colors whitespace-nowrap"
            >
              <i className="ri-mail-line"></i>
              werbung@helvetik-journal.ch
            </a>
            <a
              href="tel:+41441234568"
              className="flex items-center gap-2 px-6 py-3 border border-hj-gray-border text-hj-dark text-sm font-medium rounded-lg hover:bg-hj-gray-light transition-colors whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              +41 44 123 45 68
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}